import { useEffect, useState, type FormEvent } from "react";
import { Button, Card, Cluster, FormField, Input, Select, Stack, guardSubmit } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { api, ApiError } from "./api";
import { notifySaved } from "./savedToast";
import { Flash } from "./Flash";

interface NetworkBlock {
  id: string;
  kind: "ip" | "country";
  value: string;
  note: string | null;
  created_at: string | null;
}

const COUNTRIES: Array<{ id: string; label: string }> = [
  { id: "AU", label: "Australia" },
  { id: "US", label: "United States" },
  { id: "GB", label: "United Kingdom" },
  { id: "NZ", label: "New Zealand" },
  { id: "CA", label: "Canada" },
  { id: "IE", label: "Ireland" },
  { id: "SG", label: "Singapore" },
  { id: "IN", label: "India" },
  { id: "PH", label: "Philippines" },
  { id: "ZA", label: "South Africa" },
  { id: "NG", label: "Nigeria" },
  { id: "KE", label: "Kenya" },
  { id: "BR", label: "Brazil" },
  { id: "MX", label: "Mexico" },
  { id: "DE", label: "Germany" },
  { id: "FR", label: "France" },
  { id: "NL", label: "Netherlands" },
  { id: "RU", label: "Russia" },
  { id: "CN", label: "China" },
  { id: "HK", label: "Hong Kong" },
  { id: "JP", label: "Japan" },
  { id: "KR", label: "South Korea" },
  { id: "VN", label: "Vietnam" },
  { id: "ID", label: "Indonesia" },
  { id: "PK", label: "Pakistan" },
  { id: "BD", label: "Bangladesh" },
  { id: "UA", label: "Ukraine" },
  { id: "BY", label: "Belarus" },
  { id: "IR", label: "Iran" },
  { id: "KP", label: "North Korea" },
];

export function AccessPage() {
  const [blocks, setBlocks] = useState<NetworkBlock[]>([]);
  const [lookup, setLookup] = useState("headers");
  const [kind, setKind] = useState<"ip" | "country">("ip");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const row = await api.get<{ blocks: NetworkBlock[]; country_lookup: string }>("/operator/network-blocks");
      setBlocks(row.blocks);
      setLookup(row.country_lookup);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network blocks could not be loaded.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onAdd(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post("/operator/network-blocks", {
        kind,
        value: value.trim(),
        note: note.trim() || null,
      });
      setValue("");
      setNote("");
      await load();
      notifySaved("Network block saved.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That block could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  async function onRemove(id: string) {
    setError(null);
    try {
      await api.delete(`/operator/network-blocks/${id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That block could not be removed.");
    }
  }

  const ips = blocks.filter((row) => row.kind === "ip");
  const countries = blocks.filter((row) => row.kind === "country");

  return (
    <Stack gap={5}>
      <div>
        <h2>App Security</h2>
        <p className="admin-muted">
          Block countries and IP ranges from the org console, admin API, signup, and sign-in.
          Marketing pages on saleslox.com stay public. You cannot add a block that matches the
          network you are using now.
        </p>
        <p className="admin-muted">
          {lookup === "headers_and_geoip"
            ? "Country is read from trusted proxy headers and the GeoIP database."
            : "Country is read from Cloudflare or CloudFront headers on trusted proxies, or from the GeoIP database when one is mounted."}
        </p>
      </div>
      {error ? <Flash tone="error">{error}</Flash> : null}
      <Card>
        <form onSubmit={guardSubmit(onAdd)}>
          <Stack gap={3}>
            <TipHeading
              label="Add a block"
              tip="Deny an IP/CIDR or a whole country from console, admin API, signup, and sign-in. Marketing stays public."
            />
            <FormField
              label="Type"
              htmlFor="network-block-kind"
              tooltip="Choose IP/CIDR for a host or range, or Country to deny every request from that country code."
            >
              <Select
                id="network-block-kind"
                value={kind}
                onChange={(event) => {
                  setKind(event.target.value === "country" ? "country" : "ip");
                  setValue("");
                }}
              >
                <option value="ip">IP address or CIDR</option>
                <option value="country">Country</option>
              </Select>
            </FormField>
            {kind === "country" ? (
              <FormField
                label="Country"
                htmlFor="network-block-country"
                tooltip="ISO country to block. Matched from trusted proxy headers and/or GeoIP on the request."
              >
                <Select id="network-block-country" value={value} onChange={(event) => setValue(event.target.value)} required>
                  <option value="">Choose a country</option>
                  {COUNTRIES.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.label} ({row.id})
                    </option>
                  ))}
                </Select>
              </FormField>
            ) : (
              <FormField
                label="IP or CIDR"
                htmlFor="network-block-ip"
                hint="Examples: 203.0.113.10 or 198.51.100.0/24"
                tooltip="Single address or CIDR range to deny. Cannot match the network you are calling from."
              >
                <Input id="network-block-ip" value={value} onChange={(event) => setValue(event.target.value)} required autoComplete="off" />
              </FormField>
            )}
            <FormField
              label="Note (optional)"
              htmlFor="network-block-note"
              tooltip="Operator reminder for why this block exists (ticket, incident, abuse report)."
            >
              <Input id="network-block-note" value={note} onChange={(event) => setNote(event.target.value)} />
            </FormField>
            <Button type="submit" disabled={saving || !value.trim()}>
              {saving ? "Saving…" : "Block"}
            </Button>
          </Stack>
        </form>
      </Card>
      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Blocked IPs"
            tip="Active IP and CIDR denials. Remove an entry to restore access from that network."
          />
          {ips.length === 0 ? <p className="admin-muted">None.</p> : null}
          {ips.map((row) => (
            <Cluster key={row.id} gap={3} align="center" justify="between">
              <div>
                <strong>{row.value}</strong>
                {row.note ? <p className="admin-muted">{row.note}</p> : null}
              </div>
              <Button variant="ghost" onClick={() => onRemove(row.id)}>
                Remove
              </Button>
            </Cluster>
          ))}
        </Stack>
      </Card>
      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Blocked countries"
            tip="Active country denials. Remove an entry to allow traffic from that country again."
          />
          {countries.length === 0 ? <p className="admin-muted">None.</p> : null}
          {countries.map((row) => (
            <Cluster key={row.id} gap={3} align="center" justify="between">
              <div>
                <strong>{row.value}</strong>
                {row.note ? <p className="admin-muted">{row.note}</p> : null}
              </div>
              <Button variant="ghost" onClick={() => onRemove(row.id)}>
                Remove
              </Button>
            </Cluster>
          ))}
        </Stack>
      </Card>
    </Stack>
  );
}
